import {
  ButtonAppearance,
  ButtonCustom,
  CustomTextInput,
  DisableFocusWrapper,
  Text,
  TextAppearance,
  TextColor,
  TextInputAppearance,
  buttonPaddingGenerator,
} from '@vw-marketing/us-components';
import React, { useState, useEffect } from 'react';
import { ErrorState } from '..';
import { useTrackingManager } from '../../../services/use-tracking-manager';
import { InputContainer, InputZipWrapper, SubmitButtonWrapper } from './styled';

interface RecallsInputProps {
  onSubmitHandler: (vin: string) => void;
  errorRequest: ErrorState;
}

// Main error messages
const errorMessages = {
  invalidInput:
    'Letters O, I, or Q should not be used. Please check your VIN and try again.',
  errorRequest:
    'This is not a recognizable Volkswagen VIN. Please check your VIN and try again.',
};

const RecallsInput = ({
  onSubmitHandler,
  errorRequest,
}: RecallsInputProps): JSX.Element => {
  const trackingManager = useTrackingManager();
  const [vin, setVin] = useState('');
  const [errorState, setErrorState] = useState<ErrorState>({ state: false });
  const [validVin, setValidVin] = useState<boolean>(false);
  const vinCharacters = 17;
  const placeholder = 'Enter your 17-character VIN';
  const vinRegex = /[iqo]/gi;

  /**
   * Handler input change event
   * @param event
   */
  const onChangeHandler = (event: React.SyntheticEvent<HTMLInputElement>) => {
    if (event.currentTarget.value.length <= vinCharacters) {
      const newValue = event.currentTarget.value.toLocaleUpperCase();
      setVin(newValue);
    }
  };

  // If change VIN
  useEffect(() => {
    const invalidVin = vinRegex.test(vin);
    setErrorState({ state: invalidVin, message: errorMessages.invalidInput });
    vinCharacters === vin.length ? setValidVin(true) : setValidVin(false);
  }, [vin]);

  // If there are error from request response
  useEffect(() => {
    if (errorRequest.state) {
      const message = errorRequest.message
        ? errorRequest.message
        : errorMessages.errorRequest;
      setErrorState({ ...errorRequest, message });
      trackingManager.trackRecallsGlobalEvent(
        'VWBasic_VINFormErrorMessage_Load',
        {
          vin,
          FormName: 'Recall request',
          message: 'This is not a recognizable Volkswagen VIN.',
          code: '400',
          FormStart: true,
          FormType: 'otherform',
          FormVersion: '1',
        },
        undefined,
      );
    } else {
      setErrorState({ state: false });
    }
  }, [errorRequest]);

  return (
    <InputZipWrapper>
      <InputContainer>
        <CustomTextInput
          name={'vin'}
          appearance={
            errorState.state
              ? TextInputAppearance.Error
              : TextInputAppearance.Default
          }
          placeholder={placeholder}
          value={vin}
          onChange={onChangeHandler}
          errorText={errorState.message}
          useCustomZipText={true}
          onKeyPress={() => (validVin ? onSubmitHandler(vin) : null)}
          data-testid="recall-input"
        />
        <SubmitButtonWrapper>
          <DisableFocusWrapper>
            <ButtonCustom
              appearance={ButtonAppearance.Primary}
              onClick={onSubmitHandler.bind(null, vin)}
              padding={buttonPaddingGenerator(0, 52, 52, 52)}
              disabled={!validVin}
              data-testid="recall-button"
            >
              <Text
                appearance={TextAppearance.label100}
                color={TextColor.inherit}
              >
                Submit
              </Text>
            </ButtonCustom>
          </DisableFocusWrapper>
        </SubmitButtonWrapper>
      </InputContainer>
    </InputZipWrapper>
  );
};

export default RecallsInput;
